import { useEffect, useRef, useState } from 'react';

export function useDraggableClamp({
  enabled = true,
  clampMargin = 12,
  initial = { x: 0, y: 0 },
} = {}) {
  const [pos, setPos] = useState(initial);
  const [dragging, setDragging] = useState(false);

  const hostRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setPos(initial);
  }, [initial.x, initial.y]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onBlur = () => setDragging(false);
    const onVis = () => { if (document.visibilityState !== 'visible') setDragging(false); };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const onPointerDown = (e) => {
    if (!enabled) return;
    hostRef.current?.setPointerCapture?.(e.pointerId);
    setDragging(true);
    const rect = hostRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    offsetRef.current = { x: e.clientX - cx, y: e.clientY - cy };
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!enabled || !dragging) return;
    const rect = hostRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const desiredX = e.clientX - (vw / 2) - offsetRef.current.x;
    const desiredY = e.clientY - (vh / 2) - offsetRef.current.y;

    const halfW = rect.width / 2;
    const halfH = rect.height / 2;

    const minX = -(vw / 2) + halfW + clampMargin;
    const maxX =  (vw / 2) - halfW - clampMargin;
    const minY = -(vh / 2) + halfH + clampMargin;
    const maxY =  (vh / 2) - halfH - clampMargin;

    const clampedX = Math.max(minX, Math.min(maxX, desiredX));
    const clampedY = Math.max(minY, Math.min(maxY, desiredY));

    setPos({ x: clampedX, y: clampedY });
  };

  const onPointerUp = (e) => {
    if (!enabled || !dragging) return;
    setDragging(false);
    hostRef.current?.releasePointerCapture?.(e.pointerId);
  };

  return {
    hostRef,
    pos,
    dragging,
    props: {
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
    headerDragProps: {
      onPointerDown,
    },
    reset: (x = 0, y = 0) => setPos({ x, y }),
  };
}
